import { useState, useCallback } from "react";

import { retryQueryOnInternalServerError } from "app/pages/sales-intelligence/query/utils";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

import { aiAgentQueryKeys } from "../../../queries/queryKeyFactory";
import { useSSEStream } from "../../../queries/useSSEStream";
import ParseEventStreamService from "../../../services/streamParser/streamParserService";
import type { StreamEvent } from "../../../services/streamParser/types";
import type {
    LeadGeneratorAgentMessagePayload,
    LeadGeneratorAgentPayload,
    LeadGeneratorThreadData,
} from "../types/dto";

type HookProps = {
    isEnabled: boolean;
    threadId: string;
    messageId: number;
    messagePayload: LeadGeneratorAgentPayload;
};

type HookValue = {
    data: string;
    isLoading: boolean;
    isPending: boolean;
    agentMessageId: string;
    threadId: string;
    stop: () => Promise<void>;
};

const useLeadGeneratorChatQuery = ({ isEnabled, threadId, messagePayload, messageId }: HookProps): HookValue => {
    const [data, setData] = useState("");
    const [agentMessageId, setAgentMessageId] = useState("");

    const handleStreamEvent = useCallback(
        (event: StreamEvent) => {
            if (ParseEventStreamService.isPartialMessageStreamEvent(event)) {
                const { content, id } = event.data[0];

                if (id && id !== agentMessageId) {
                    setAgentMessageId(id);
                    setData(content);
                } else {
                    setData((prev) => prev + content);
                }
            }

            if (ParseEventStreamService.isErrorStreamEvent(event)) {
                sentryErrorTrackerWithScope({
                    errorMessage: "Error while parsing stream",
                    e: new Error("Streaming Error", { cause: event }),
                    params: {
                        extra: {
                            threadId,
                            messagePayload,
                        },
                    },
                });
            }
        },
        [agentMessageId],
    );

    const { isLoading, isPending, stop } = useSSEStream<LeadGeneratorAgentMessagePayload, LeadGeneratorThreadData>({
        enabled: isEnabled,
        queryKey: aiAgentQueryKeys.leadGenerator.chat(threadId, messageId, messagePayload),
        payload: {
            threadId,
            ...messagePayload,
        },
        queryOptions: {
            retry: retryQueryOnInternalServerError,
        },
        fetcher: salesApiService.aiAgent.leadGenerator.postAIAgentMessage,
        onEvent: handleStreamEvent,
    });

    return { data, isLoading, isPending, stop, agentMessageId, threadId };
};

export default useLeadGeneratorChatQuery;
